import React, {useState, useMemo} from 'react';
import { useDispatch } from 'react-redux';
import styles from './ManagementDropdownCard.module.css';
import PerinPatchModalityTypeSection from './PerinPatchModalityTypeSection';
import {
    convertStartTime,
    clampPerinPatchMinuteFrequency,
    getDefaultMinFrequencyWhenSwitchingToMinutes,
    isBloodPressureModality,
    shouldSkipBpOnApplyAllFrequency,
} from '../utils/programUtils';
import { showAlert } from './PerinHealthAlertSlice';

const PerinPatchSetupCard = (props) => {
    const {
        id, 
        modalities,
        setModality,
        modalityState, 
        errorShowing, 
        disabled, 
        viewOnly, 
        allDevices
    } = props;

    const dispatch = useDispatch();

    const groupedModalities = Object.values(
        modalities
            .reduce((acc, modality) => {
                const key = `${modality.modalityType}|${modality.modalityTypeDisplayName}`;
                if (!acc[key]) {
                    acc[key] = {
                        id: modality.modalityType,
                        name: modality.modalityTypeDisplayName,
                        modalities: []
                    };
                }
                acc[key].modalities.push(modality);
                return acc;
            }, {})
    );

    const [selectedModalityType, setSelectedModalityType] = useState(groupedModalities[0] ?? 0);
    const [selectedVital, setSelectedVital] = useState(groupedModalities[0].modalities[0] ?? 0);

    // const data = modalityState.find((item)=> (item.deviceId === id && item.deviceModalityId === selectedModalityType.modalities[0].deviceModalityId));
    const data = useMemo(() => {
        return modalityState.find((item) => 
            item.deviceId === id && 
            item.deviceModalityId === selectedVital.deviceModalityId
        );
    }, [selectedVital, modalityState, id]);

    const updateStartTime = (name, value, startTimeView) => {
        // Calculate ISO string and dispatch action
        const isoString = convertStartTime(
            name === "startInt" ? value : startTimeView.startInt,
            name === "interval" ? value : startTimeView.interval
        );

        // update all modalities in that modality group
        selectedModalityType.modalities.forEach((modality)=> {
            const currentVitalData = modalityState.find((item)=> (item.deviceId === id && item.deviceModalityId === modality.deviceModalityId));
            let tempData = {...currentVitalData};
            tempData['startTime'] = isoString;
            dispatch(setModality(tempData));
        });
    };

    const isCurrentGroupBp = isBloodPressureModality(selectedModalityType?.id);
    const handleFrequency = (e) => {
        let value = e.target.value;
        if (value === "" || isNaN(Number(value))) {
            value = ""; // Reset if empty or invalid
        } else {
            value = Number(value); // Convert to number
            if (data.interval === "Min") {
                value = clampPerinPatchMinuteFrequency(value, isCurrentGroupBp ? 'bp' : null);
            }
        }
        selectedModalityType.modalities.forEach((modality)=> {
            const currentVitalData = modalityState.find((item)=> (item.deviceId === id && item.deviceModalityId === modality.deviceModalityId));
            let tempData = {...currentVitalData};
            tempData['frequency'] = value;
            if (data["interval"] === "") {
                tempData['interval'] = "Day";
            };
            dispatch(setModality(tempData));
        });
    };

    const handleEnabledChange = (e) => {
        const checked = e.target.checked;
        selectedModalityType.modalities.forEach((modality)=> {
            const currentVitalData = modalityState.find((item)=> (item.deviceId === id && item.deviceModalityId === modality.deviceModalityId));
            let tempData = {...currentVitalData};
            tempData['frequency'] = checked ? 1 : 0;
            tempData['interval'] = checked ? "Day" : "";
            if (!checked) {
                tempData['lowerLimit'] = 0;
                tempData['upperLimit'] = 0;
                tempData['targetValue'] = 0;
            };
            dispatch(setModality(tempData));
        });

        // Enabling BP also re-enables every other Perin Patch modality that was turned off
        // while BP was active (their Enabled toggle was locked). Only flip rows that are
        // currently disabled (frequency === 0) so we never clobber an already-active schedule.
        if (checked && isCurrentGroupBp) {
            groupedModalities.forEach((modalityType) => {
                if (modalityType.id === 'bp') return;
                modalityType.modalities.forEach((modality) => {
                    const currentVitalData = modalityState.find(
                        (item) => item.deviceId === id && item.deviceModalityId === modality.deviceModalityId
                    );
                    if (!currentVitalData || currentVitalData.frequency !== 0) return;
                    const tempData = { ...currentVitalData };
                    tempData.frequency = 1;
                    tempData.interval = 'Day';
                    dispatch(setModality(tempData));
                });
            });
        }
    };


    const handleIntervalChange = (e) => {
        selectedModalityType.modalities.forEach((modality)=> {
            const currentVitalData = modalityState.find((item)=> (item.deviceId === id && item.deviceModalityId === modality.deviceModalityId));
            let tempData = {...currentVitalData};
            if (e.target.value === "Min") {
                const groupModalityType = selectedModalityType?.id;
                tempData.frequency = getDefaultMinFrequencyWhenSwitchingToMinutes(
                    tempData.frequency || 0,
                    groupModalityType
                );
            }
            tempData['interval'] = e.target.value;
            dispatch(setModality(tempData));
        });
    };

    const handleModalityTypeChange = (modalityType) => {
        setSelectedModalityType(modalityType);
        setSelectedVital(modalityType.modalities[0]);
    };

    const handleVitalChange = (vital) => {
        setSelectedVital(vital);
    };

    const applyAllSameStartTime = (newStartTime) => {
        groupedModalities.forEach(modalityType => {
            modalityType.modalities.forEach( modality => {
                const currentVitalData = modalityState.find((item)=> (item.deviceId === id && item.deviceModalityId === modality.deviceModalityId));
                let tempData = {...currentVitalData};
                tempData['startTime'] = newStartTime;
                dispatch(setModality(tempData));
            })
        })
    };

    const applyAllSameFrequency = (newFrequency, newInterval) => {
        const skipBp = shouldSkipBpOnApplyAllFrequency(newFrequency, newInterval);
        let skippedBp = false;

        groupedModalities.forEach(modalityType => {
            modalityType.modalities.forEach((modality) => {
                const currentVitalData = modalityState.find(
                    (item) => item.deviceId === id && item.deviceModalityId === modality.deviceModalityId
                );
                if (skipBp && isBloodPressureModality(currentVitalData?.modalityType)) {
                    skippedBp = true;
                    return;
                }
                const tempData = { ...currentVitalData };
                tempData.frequency = newFrequency;
                tempData.interval = newInterval;
                dispatch(setModality(tempData));
            });
        });

        if (skippedBp) {
            dispatch(showAlert({
                header: 'BP Interval not updated',
                message:
                    'Blood Pressure requires a minimum interval of 15 minutes. Other modalities were updated; Blood Pressure was unchanged.',
                type: 'warning',
                dismissable: true,
            }));
        }

        return skippedBp;
    };

    const checkIfModalityTypeHasError = (item) => {
        const limitErrors = errorShowing.modalityErrors.some(id => item.modalities.some(mod => mod.deviceModalityId === id))
        const intervalErrors = errorShowing.perinModalityErrors.some(id => item.modalities.some(mod => mod.deviceModalityId === id))
        return limitErrors || intervalErrors;
    }

    return (
        <div style={{display:"flex", flexDirection:"column", gap:"7px", width:"100%", height:"100%"}}>
            <div className={styles.ModalitiesList}>
                {groupedModalities.map((item) => (
                    <div 
                        key={item.id} 
                        onClick={() => handleModalityTypeChange(item)} 
                        style={checkIfModalityTypeHasError(item) ? {color: "#DC3545"} : {}}
                        className={selectedModalityType.id===item.id ? styles.ModalityLabelActive : styles.ModalityLabel}
                    >
                        {item.name}
                    </div>
                ))}
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:"7px", padding:"5px 0 0px 20px"}}>
                {groupedModalities.map((item, index) => (
                    <div key={index} hidden={item.id===selectedModalityType.id ? false : true}>
                        <PerinPatchModalityTypeSection 
                            modalityVitals={item.modalities}
                            selectedVital={selectedVital}
                            handleChange={handleVitalChange}
                            updateStartTime={updateStartTime}
                            handleFrequency={handleFrequency}
                            handleIntervalChange={handleIntervalChange}
                            viewOnly={viewOnly}
                            errorShowing={errorShowing}
                            disabled={disabled}
                            allDevices={allDevices} //all devices available to user
                            modalityState={modalityState} // current modalities in enrollment/program
                            setModality={setModality}
                            applyAllSameStartTime={applyAllSameStartTime}
                            applyAllSameFrequency={applyAllSameFrequency}
                            handleEnabledChange={handleEnabledChange}
                        />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default PerinPatchSetupCard;